import { Alert, AlertTitle, CircularProgress, Stack, Typography } from '@mui/material';
import useLocationQuery from '@hooks/useLocationQuery';
import { useEffect, useState } from 'react';
import { getPaymentInfoBySessionId, listenToPaymentChanges } from '@modules/supabase/payments';
import { PAYMENT_STATUS, PaymentInfo } from '@type/db-types';

// This component is used to display the payment status of the user
// immediately after they have completed the payment process.
// This is why it only checks the query parameter `stripeSession`
export default function PaymentStatusBanner() {
    const query = useLocationQuery();
    const stripeSession = query.get('stripeSession');
    const [payment, setPayment] = useState<PaymentInfo | null>(null);

    // console.log(`PaymentStatusBanner`, { stripeSession, payment });

    useEffect(() => {
        if (stripeSession) {
            getPaymentInfoBySessionId(stripeSession).then((payment) => setPayment(payment));
            // console.log(`stripeSession listenToPaymentChanges`, { stripeSession });
            return listenToPaymentChanges({
                sessionId: stripeSession,
                onUpdate: (payment) => setPayment(payment),
            });
        }
    }, [stripeSession]);

    // If they have not recently completed a payment, do not show anything
    if (!stripeSession) return null;

    if (stripeSession && payment?.status === PAYMENT_STATUS.PAID) {
        return (
            <Alert severity="success" sx={{ padding: 2, marginBottom: 2 }}>
                <AlertTitle>Registration Complete</AlertTitle>
                {`Congratulations! Your payment has been processed successfully and you are now registered for the event.`}
            </Alert>
        );
    } else if (
        stripeSession &&
        payment?.status &&
        [PAYMENT_STATUS.ERRORED, PAYMENT_STATUS.CANCELLED, PAYMENT_STATUS.UNPAID].includes(
            payment.status,
        )
    ) {
        return (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
                <AlertTitle>Payment Failed</AlertTitle>
                {`Please try again or contact our support team`}
            </Alert>
        );
    }

    // Show loading spinner and wait for payment to be processed
    return (
        <Alert severity="info" icon={false} sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" paddingLeft={1} spacing={2}>
                <CircularProgress color="info" size={16} />
                <Typography>Processing payment...</Typography>
            </Stack>
        </Alert>
    );
}
